import z from "zod";

import type {
  ButtonSize,
  ButtonVariant,
} from "@frontend/components/atoms/Button/Button.types";

import type { MenuProps, MenuSize } from "./Menu.types";

export const menuItemOptionSchema = z.object({
  value: z.string().describe("Unique identifier for this item."),
  label: z.string().describe("Visible label shown for the item."),
  icon: z
    .string()
    .optional()
    .describe("Optional icon name shown next to the label."),
  disabled: z.boolean().optional(),
});

export type MenuItemOption = z.infer<typeof menuItemOptionSchema>;

export const menuDataSchema = z.object({
  defaultOpen: z
    .string()
    .optional()
    .transform((value) => value !== undefined && value !== "false")
    .describe(
      "Initial open state. Read once, at mount — use `open` instead to change it after the initial render.",
    ),
  open: z
    .string()
    .optional()
    .transform((value) => (value === undefined ? undefined : value !== "false"))
    .describe(
      "Current open state. Settable at any time — e.g. `el.setAttribute('open', '')` or `el.open()` — to open/close the menu externally.",
    ),
  size: z.custom<MenuSize>().optional(),
  align: z.custom<NonNullable<MenuProps["align"]>>().optional(),
  side: z.custom<NonNullable<MenuProps["side"]>>().optional(),
  arrow: z
    .string()
    .optional()
    .transform((value) => value !== undefined && value !== "false"),
  triggerLabel: z
    .string()
    .optional()
    .describe(
      "Label text for the trigger button, read from <rwc-menu-trigger>'s textContent if not set. Rendered directly by React (not slotted) — see readMenuSlotted for why: a light-DOM element slotted into the trigger button breaks Base UI's shadow-DOM-aware `contains()` outside-click check, since a slotted node's root stays the light DOM rather than the shadow tree it's projected into.",
    ),
  triggerIcon: z
    .string()
    .optional()
    .describe(
      "Parsed from the `icon` attribute on <rwc-menu-trigger> — not a Twig-authored attribute on <rwc-menu> itself.",
    ),
  triggerVariant: z
    .custom<ButtonVariant>()
    .optional()
    .describe(
      "Parsed from the `variant` attribute on <rwc-menu-trigger> — not a Twig-authored attribute on <rwc-menu> itself.",
    ),
  triggerSize: z
    .custom<ButtonSize>()
    .optional()
    .describe(
      "Parsed from the `size` attribute on <rwc-menu-trigger> — not a Twig-authored attribute on <rwc-menu> itself.",
    ),
  items: z
    .array(menuItemOptionSchema)
    .default([])
    .describe(
      "Items parsed from the light DOM (<rwc-menu-item> elements inside <rwc-menu-content>) — not a Twig-authored attribute.",
    ),
  onOpenChange: z
    .custom<(open: boolean) => void>()
    .optional()
    .describe(
      "Internal callback bound by readMenuSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
  onSelect: z
    .custom<(value: string, item: MenuItemOption) => void>()
    .optional()
    .describe(
      "Internal callback bound by readMenuSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
});

export type MenuDataProps = z.infer<typeof menuDataSchema>;
